import type { Geometry } from "geojson";
import { mockLayers } from "../global/constants/layers";
import { layerStyleVariations } from "../global/layerStyles/variations";
import { CATEGORICAL_LEGEND_FIELDS } from "../global/constants/categoricalLegends";
import type { ContextLegend } from "../Components/ReportComponent/model/types";
import {
  ApiGetLegendCounts,
  type LegendCountsPayload,
  type LegendLayerCountRequest,
  type LegendItemCountRequest,
} from "./api";

function isRasterStatLayer(name: string): name is "ghi" | "windspeedtest" {
  return name === "ghi" || name === "windspeedtest";
}

function buildCQLFilter(properties: Record<string, unknown>): string {
  return Object.entries(properties)
    .map(([key, val]) =>
      typeof val === "string" ? `${key}='${val}'` : `${key}=${val}`
    )
    .join(" AND ");
}

// A layer regioes_imediatas_pe tem 18 regiões imediatas; as variações no frontend
// agrupam-nas em 5 mesorregiões usando um cd_rgi representativo. Este mapa garante
// que o filtro CQL cubra todas as regiões da mesorregião, não só a representativa.
const REGIOES_IMEDIATAS_MESORREGIAO_GROUPS: Record<string, string[]> = {
  "260001": ["260001"],
  "260002": ["260002", "260003", "260005", "260006", "260007", "260018"],
  "260009": ["260004", "260008", "260009", "260010", "260012"],
  "260013": ["260011", "260013", "260014", "260016", "260017"],
  "260015": ["260015"],
};

function buildLegendCountsPayload(
  legends: ContextLegend[],
  geometry: Geometry | null,
  isAreaReport: boolean
): LegendCountsPayload {
  const layers: LegendLayerCountRequest[] = [];

  for (const legend of legends) {
    const layer = mockLayers.find((l) => l.title === legend.layerName);
    if (!layer) continue;
    // Camadas WMS estilizadas no servidor: as classes vêm do SLD, sem filtro CQL
    // correspondente, então não há contagem a pedir.
    if (layer.type === "wms") continue;

    if (layer.type === "raster" || layer.type === "particle") {
      if (!isRasterStatLayer(layer.layerName)) continue;
      layers.push({
        legend_id: legend.layerName,
        geoserver_type_name: layer.layerName,
        category: legend.category,
        layer_type: "raster",
        raster_id: layer.layerName as "ghi" | "windspeedtest",
        items: [{ label: legend.layerName, cql_filter: null }],
      });
      continue;
    }

    const workspace = layer.workspace ?? "pepe-atlas";
    const typeName = `${workspace}:${layer.layerName}`;
    const variations = layerStyleVariations[layer.layerName];

    if (layer.layerName === "regioes_imediatas_pe") {
      const items: LegendItemCountRequest[] = (variations ?? []).map(
        (variation) => {
          const rawCdRgi = variation.properties.cd_rgi;
          if (typeof rawCdRgi !== "string") {
            return { label: variation.label, cql_filter: null };
          }
          const group = REGIOES_IMEDIATAS_MESORREGIAO_GROUPS[rawCdRgi] ?? [
            rawCdRgi,
          ];
          const cql_filter =
            group.length === 1
              ? `cd_rgi='${group[0]}'`
              : `cd_rgi IN (${group.map((c) => `'${c}'`).join(",")})`;
          return { label: variation.label, cql_filter, cd_rgi: rawCdRgi };
        }
      );
      layers.push({
        legend_id: legend.layerName,
        geoserver_type_name: typeName,
        category: legend.category,
        layer_type: "regioes_imediatas",
        items,
      });
      continue;
    }

    // Camadas coloridas por hash (vocação, APL): as classes só existem depois
    // da legenda ser hidratada (hydrateCategoricalLegends), então o filtro CQL
    // de cada item vem do próprio valor do campo, não de `layerStyleVariations`.
    const categoricalField = CATEGORICAL_LEGEND_FIELDS[layer.layerName];

    const items: LegendItemCountRequest[] = categoricalField
      ? legend.items.map((item) => ({
          label: item.label,
          cql_filter: buildCQLFilter({ [categoricalField]: item.label }),
        }))
      : variations
      ? variations.map((v) => ({
          label: v.label,
          cql_filter: buildCQLFilter(v.properties as Record<string, unknown>),
        }))
      : [{ label: legend.layerName, cql_filter: layer.cqlFilter ?? null }];

    layers.push({
      legend_id: legend.layerName,
      geoserver_type_name: typeName,
      category: legend.category,
      layer_type: "vector",
      is_point_geometry: Boolean(layer.style?.customIcon),
      count_group_by_field: layer.countGroupByField,
      items,
    });
  }

  return { geometry, is_area_report: isAreaReport, layers };
}

function mergeCountsIntoLegends(
  legends: ContextLegend[],
  response: Awaited<ReturnType<typeof ApiGetLegendCounts>>
): ContextLegend[] {
  const resultMap = new Map(response.layers.map((r) => [r.legend_id, r]));

  return legends.map((legend) => {
    const result = resultMap.get(legend.layerName);
    if (!result) return legend;

    const itemMap = new Map(result.items.map((i) => [i.label, i]));
    const enrichedItems = legend.items.map((item) => {
      const r = itemMap.get(item.label);
      if (!r) return item;
      return {
        ...item,
        count: r.count,
        areaPct: r.area_pct,
        areaKm2: r.area_km2,
        range: r.range
          ? {
              min: r.range.min,
              max: r.range.max,
              mean: r.range.mean,
              unit: r.range.unit,
            }
          : item.range,
      };
    });

    return { ...legend, items: enrichedItems };
  });
}

export async function enrichLegendsWithCounts(
  legends: ContextLegend[],
  geometry: Geometry | null,
  isAreaReport: boolean
): Promise<ContextLegend[]> {
  const payload = buildLegendCountsPayload(legends, geometry, isAreaReport);

  if (payload.layers.length === 0) return legends;

  try {
    const response = await ApiGetLegendCounts(payload);
    return mergeCountsIntoLegends(legends, response);
  } catch {
    return legends;
  }
}
